import * as THREE from "three";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import { GLTFLoader } from "../../external/GLTFLoader.js";
import { KTX2Loader } from "three/examples/jsm/loaders/KTX2Loader.js";
import { MeshoptDecoder } from "../third-party/threejs/meshopt_decoder.js";
import { NexusObject } from "../../external/nexus_three";
import { Nexus } from "../../external/nexus";
import { SceneElement } from "../data-structures/scene-element";
import { TextureCache } from "./texture-cache";

declare const proj4: any;

/**
 * Manager responsible for managing 3D mesh models including
 * loading, raycasting, and visibility management.
 */
export class MeshManager {
	private readonly viewer: any;
	private readonly dracoLoader: DRACOLoader;
	private readonly gltfLoader: GLTFLoader;
	private readonly matcapMaterial: THREE.MeshMatcapMaterial;
	public readonly meshesGroup: THREE.Group;
	public meshes: SceneElement[] = [];

	constructor(viewer: any) {
		this.viewer = viewer;
		this.matcapMaterial = new THREE.MeshMatcapMaterial({ flatShading: true });
		this.matcapMaterial.matcap = new TextureCache().load("/images/matcap.png");

		// Initialize meshes group
		this.meshesGroup = new THREE.Group();
		this.meshesGroup.name = "ModelsGroup";
		this.viewer.scene.scene.add(this.meshesGroup);

		// Initialize DRACO loader
		this.dracoLoader = new DRACOLoader();
		this.dracoLoader.setDecoderConfig({ type: "js" });
		this.dracoLoader.setDecoderPath("/libs/draco/");
		this.dracoLoader.preload();

		// Initialize Nexus cache
		const gl = this.viewer.renderer.getContext();
		if (gl && Nexus.setMaxCacheSize) {
			Nexus.setMaxCacheSize(gl, 2048 * (1 << 20)); // 2GB
		}

		// Initialize GLTF loader
		const ktx2Loader = new KTX2Loader();
		ktx2Loader.setTranscoderPath("/libs/basis/");
		ktx2Loader.detectSupport(this.viewer.renderer);

		this.gltfLoader = new GLTFLoader(THREE.DefaultLoadingManager);
		this.gltfLoader.setKTX2Loader(ktx2Loader);
		this.gltfLoader.setDRACOLoader(this.dracoLoader);
		this.gltfLoader.setMeshoptDecoder(MeshoptDecoder);
	}

	public loadModel(
		url: string,
		name: string,
		uuid: string,
		visible = false,
		transformationUrl: string
	): Promise<THREE.Object3D> {
		const nexus = url.endsWith(".nxs");
		return new Promise((resolve) => {
			if (nexus) {
				fetch(transformationUrl)
					.then((response) => response.json())
					.then((transformation) => {
						const model = new NexusObject(
							url,
							function () {
								model.visible = visible;
								resolve(model);
							},
							function () {
								// on reload
							},
							this.viewer.renderer,
							this.matcapMaterial,
							this.viewer.scene.scene,
							this.viewer.scene.getActiveCamera()
						);

						if (this.viewer.convertToWGS84) {
							const newModelPosition = proj4(
								this.viewer.projectCoordinateSystem,
								this.viewer.mapCoordinateSystem
							).forward([
								model.position.x,
								model.position.y,
								model.position.z,
							]);
							model.position.set(
								newModelPosition[0],
								newModelPosition[1],
								newModelPosition[2]
							);
						}

						model.name = name;
						model.uuid = uuid;
						model.position.set(
							transformation.origin[0],
							transformation.origin[1],
							transformation.origin[2]
						);

						this.preloadTextures(model);
						this.computeRaycastBVH(model);

						this.meshes.push(model as any);

						model.geometry.computeVertexNormals();

						if (visible) {
							this.meshesGroup.add(model);
							model.visible = true;
						} else {
							model.visible = false;
						}

						this.viewer.updateLayers();
						this.viewer.updateGroundPosition();
						this.viewer.rebuildAllVolumeConnectors();
					})
					.catch((error) => {
						console.error("Error fetching transform data:", error);
					});
			} else {
				this.gltfLoader.load(
					url,
					(o) => {
						const model = o.scene;
						if (uuid) {
							model.uuid = uuid;
						}

						if (this.viewer.convertToWGS84) {
							const newModelPosition = proj4(
								this.viewer.projectCoordinateSystem,
								this.viewer.mapCoordinateSystem
							).forward([
								model.position.x,
								model.position.y,
								model.position.z,
							]);
							model.position.set(
								newModelPosition[0],
								newModelPosition[1],
								newModelPosition[2]
							);
						}
						this.setUnlitMaterials(model);
						this.preloadTextures(model);

						model.name = "model";
						(model as any).elementType = "mesh";
						this.meshes.push(model as any);
						this.computeRaycastBVH(model);
						model.name = name;

						if (visible) {
							this.meshesGroup.add(model);
							model.visible = true;
						} else {
							model.visible = false;
						}

						this.viewer.updateLayers();
						this.viewer.updateGroundPosition();
						this.viewer.rebuildAllVolumeConnectors();

						console.log("Loaded 3dmodel", url);

						model.initTextures;
						resolve(model);
					},
					undefined,
					(errorEvent) => {
						if (errorEvent) {
							console.log("Loading model error", errorEvent);
						}
						resolve(undefined);
					}
				);
			}
		});
	}

	public showMeshes(): void {
		for (const mesh of this.meshes) {
			this.meshesGroup.add(mesh as any);
		}
	}

	public hideMeshes(): void {
		for (const mesh of this.meshes) {
			this.meshesGroup.remove(mesh as any);
		}
	}

	public getBoundingBox(): THREE.Box3 | null {
		if (this.meshes.length === 0) return null;
		return this.viewer.getBoundingBoxForObjects(this.meshes);
	}

	public findMeshById(id: string): SceneElement | undefined {
		return this.meshes.find((m) => m.uuid == id);
	}

	public async raycast(raycaster: THREE.Raycaster): Promise<THREE.Vector3 | undefined> {
		if (this.meshes.length === 0) return undefined;

		for (const mesh of this.meshes) {
			if (mesh instanceof NexusObject) {
				const intersection = await (mesh as NexusObject).raycast(raycaster);
				if (intersection) {
					return intersection;
				}
			} else {
				const intersection = raycaster.intersectObjects(this.meshes as any, true)[0];
				if (intersection) {
					return intersection.point;
				}
			}
		}

		return undefined;
	}

	public setUnlitMaterials(model: any): void {
		if (model.children) {
			for (const child of model.children) {
				this.setUnlitMaterials(child);
			}
		}
		if (model.material && !(model.material instanceof THREE.MeshBasicMaterial)) {
			if (!model.material.map) {
				model.material = this.matcapMaterial;
				if (!model.geometry.attributes.normal) {
					model.geometry.computeVertexNormals();
				}
			} else {
				const tex = model.material.map;
				const mat = new THREE.MeshBasicMaterial({
					map: tex,
				});
				mat.side = THREE.DoubleSide;
				model.material = mat;
			}
			model.needsUpdate = true;
		}
	}

	public preloadTextures(model: any): void {
		if (model.children) {
			for (const child of model.children) {
				this.preloadTextures(child);
			}
		}
		if (model.material && model.material.map) {
			this.viewer.renderer.initTexture(model.material.map);
		}
	}

	public computeRaycastBVH(model: any): void {
		if (model.children) {
			for (const child of model.children) {
				this.computeRaycastBVH(child);
			}
		}
		if (model.geometry && model.geometry.computeBoundsTree) {
			model.geometry.computeBoundsTree();
		}
	}

	public cleanup(): void {
		while (this.meshes.length > 0) {
			const e = this.meshes[0] as any;
			this.meshes.splice(0, 1);

			this.meshesGroup.remove(e);

			if (e.isNexusObject && typeof e.dispose === "function") {
				try {
					e.dispose();
				} catch (err) {
					console.warn("Error disposing Nexus mesh:", err);
				}
			}
		}
	}
}
